import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Package, AlertCircle, CreditCard } from 'lucide-react';
import { ModuleWithStatus } from '@/types/marketplace';
import { ClientLicense } from '@/types/license';
import * as LucideIcons from 'lucide-react';

interface ModuleSubscriptionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  module: ModuleWithStatus | null;
  license: ClientLicense | null;
  onConfirm: () => Promise<void>;
  isLoading?: boolean;
}

const ModuleSubscriptionDialog = ({
  open,
  onOpenChange,
  module,
  license,
  onConfirm,
  isLoading,
}: ModuleSubscriptionDialogProps) => {
  const [confirmed, setConfirmed] = useState(false);

  if (!module) return null;

  // Get icon dynamically
  const IconComponent = module.icon 
    ? (LucideIcons as any)[module.icon] || Package
    : Package;

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(price);
  };

  const technicianCount = license?.current_technicians || 1;
  const monthlyCost = module.price * technicianCount;
  const billingCycle = license?.billing_cycle || 'monthly';
  const totalCost = billingCycle === 'yearly' ? monthlyCost * 12 : monthlyCost;

  const handleConfirm = async () => {
    await onConfirm();
    setConfirmed(false);
    onOpenChange(false);
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setConfirmed(false);
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-primary/10 rounded-lg">
              <IconComponent className="h-6 w-6 text-primary" />
            </div>
            <div>
              <DialogTitle>Adicionar Módulo</DialogTitle>
              <DialogDescription>{module.name}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {module.description && (
            <p className="text-sm text-muted-foreground">
              {module.description}
            </p>
          )}

          <div className="bg-muted/50 rounded-lg p-4 space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Preço por licença/mês</span>
              <span className="font-medium">{formatPrice(module.price)}</span>
            </div>
            
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Licenças técnicas ativas</span>
              <span className="font-medium">{technicianCount}</span>
            </div>

            <div className="border-t pt-3 flex justify-between">
              <span className="font-medium">Custo adicional</span>
              <div className="text-right">
                <span className="font-bold text-lg">{formatPrice(totalCost)}</span>
                <span className="text-xs text-muted-foreground block">
                  /{billingCycle === 'yearly' ? 'ano' : 'mês'}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-start gap-3 p-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200 dark:border-amber-800">
            <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
            <p className="text-sm text-amber-800 dark:text-amber-200">
              O valor será adicionado à sua próxima fatura. Você pode gerenciar seus módulos a qualquer momento.
            </p>
          </div>

          <div className="flex items-start gap-3">
            <Checkbox
              id="confirm"
              checked={confirmed}
              onCheckedChange={(checked) => setConfirmed(checked === true)}
            />
            <Label htmlFor="confirm" className="text-sm leading-relaxed cursor-pointer">
              Confirmo a contratação do módulo <strong>{module.name}</strong> e estou ciente do custo adicional.
            </Label>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancelar
          </Button>
          <Button 
            onClick={handleConfirm} 
            disabled={!confirmed || isLoading}
          >
            <CreditCard className="h-4 w-4 mr-2" />
            {isLoading ? 'Processando...' : 'Confirmar Adesão'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ModuleSubscriptionDialog;
